import React from 'react';
import type { Kitchen } from '@rta/rta-blueprint-library';

import type { BlueprintPage } from '../types';
import {
	OVERVIEW_BODY_STYLE,
	OVERVIEW_BOLD_STYLE,
	OVERVIEW_INDENTED_RED_STYLE,
	OVERVIEW_INDENTED_STYLE,
	OVERVIEW_PLEASE_NOTE_STYLE,
	OVERVIEW_PLEASE_NOTE_WRAPPER_STYLE,
	OVERVIEW_RED_NOTE_STYLE,
	OVERVIEW_RED_STYLE,
	OVERVIEW_SECTION_SMALL_STYLE,
	OVERVIEW_SECTION_XSMALL_STYLE
} from './blueprint-kitchen-overview-page.styles';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { EditableText } from '../shared/editable-text';
import { getBaseMaterialLabel } from '../helpers/get-base-material-label';
import { getBlueprintPages } from '../helpers/get-blueprint-pages';
import { getCountertopLabel } from '../helpers/get-countertop-label';
import { getFuelSourceLabel } from '../helpers/get-fuel-source-label';
import { getIslandOverviewDescription } from '../helpers/get-island-overview-description';
import { getKitchenLayoutLabel } from '../helpers/get-kitchen-layout-label';
import { getIslandSuffix } from '../helpers/get-island-suffix';
import { getKitchenLocationLabel } from '../helpers/get-kitchen-location-label';
import { getNonStandardChanges } from '../helpers/get-non-standard-changes';
import { getOverviewAppliances } from '../helpers/get-overview-appliances';
import { getSortedIslands } from '../helpers/get-sorted-islands';
import { getTrimLabel } from '../helpers/get-trim-label';
import { splitSegments } from '../shared/reconcile-override';
import { useOverviewShrinkToFit } from '../shared/use-overview-shrink-to-fit';

export type BlueprintKitchenOverviewPageProps = {
	page: BlueprintPage;
	kitchen: Kitchen;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

// Trailing segment of the no-RTA countertop label (getCountertopLabel) shown in red.
const COUNTERTOP_ONSITE_TEXT = 'Countertop Must Be Templated Onsite';

// overview.materials used to be one combined override; pulls a field's own line back out of it so a design that was edited before the split doesn't lose that edit.
const extractMaterialsLine = (html: string | undefined, label: string): string | undefined => {
	if (html === undefined) return undefined;
	return splitSegments(html).find((segment) => segment.text.startsWith(label))?.html;
};

// overview.kitchenLocation used to be one combined override (header line + per-island lines); these pull each part back out of it for the same reason.
const extractLocationHeader = (html: string | undefined): string | undefined => {
	if (html === undefined) return undefined;
	return splitSegments(html)[0]?.html;
};

const extractLocationIslands = (html: string | undefined): string | undefined => {
	if (html === undefined) return undefined;
	const islandSegments = splitSegments(html).slice(1);
	return islandSegments.length > 0 ? islandSegments.map((segment) => segment.html).join('<br>') : undefined;
};

export const BlueprintKitchenOverviewPage = (props: BlueprintKitchenOverviewPageProps): React.ReactElement => {
	const {
		page,
		kitchen,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	const baseMaterial = getBaseMaterialLabel(kitchen.material?.surround?.displayName);
	const trimDisplay = getTrimLabel({ kitchen });
	const countertopDisplay = getCountertopLabel({ kitchen });
	const kitchenLayout = getKitchenLayoutLabel({ kitchen });
	const fuelSource = getFuelSourceLabel(kitchen.fuelchoice);
	const legacyMaterials = kitchen.blueprintOverrides?.['overview.materials'];
	const legacyMaterialsBase = kitchen.getBlueprintOverrideBase('overview.materials');
	const kitchenLocation = getKitchenLocationLabel({ kitchen });
	const { islands } = kitchen.design;
	const nonStandardChanges = getNonStandardChanges({ kitchen, pages: getBlueprintPages({ kitchen }) });
	const nonStandardChangeCount = nonStandardChanges.reduce((sum, change) => sum + change.count, 0);

	// Sort left-to-right by origin.x — matches getIslandSuffix letter assignment
	// so the overview list reads A → Z top to bottom.
	const orderedIslands = getSortedIslands(islands.children);

	// Determine which slice of islands this page renders
	const startIndex = page.overviewIslandStartIndex ?? 0;
	const endIndex = page.overviewIslandEndIndex ?? orderedIslands.length;
	const isFirstPage = startIndex === 0;
	// Footer block (Non-Standard Changes / Location / Please Note) renders on exactly
	// one page: the explicit footer page when the overview split, else the single page.
	const isLastOverviewPage = page.overviewShowFooter ?? (endIndex >= orderedIslands.length);
	const islandsToRender = orderedIslands.slice(startIndex, endIndex);

	const bodyRef = useOverviewShrinkToFit(page.overrideScope ?? String(page.pageNumber));

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			<div ref={bodyRef} style={OVERVIEW_BODY_STYLE}>
				{/* Material / Specs block — first page only */}
				{isFirstPage && (
					<>
						<div style={OVERVIEW_SECTION_SMALL_STYLE}>
							{[
								{ field: 'base', label: 'Base:', value: baseMaterial },
								{ field: 'trim', label: 'Trim:', value: trimDisplay },
								{ field: 'countertop', label: 'Countertop:', value: countertopDisplay },
								{ field: 'fuelSource', label: 'Fuel Source:', value: fuelSource }
							].map(({ field, label, value }) => {
								// "Countertop Must Be Templated Onsite" (no-RTA tops) renders red.
								const redTailIdx = value.indexOf(COUNTERTOP_ONSITE_TEXT);
								return (
									<EditableText
										key={field}
										overrideKey={`overview.materials.${field}`}
										legacyOverrideValue={extractMaterialsLine(legacyMaterials, label)}
										legacyOverrideBase={extractMaterialsLine(legacyMaterialsBase, label)}
										legacyClearKey='overview.materials'
										mergeStrategy='derived'
									>
										<span style={OVERVIEW_BOLD_STYLE}>{label} </span>
										{redTailIdx < 0 ? value : (
											<>
												{value.slice(0, redTailIdx)}
												<span style={OVERVIEW_RED_NOTE_STYLE}>{value.slice(redTailIdx)}</span>
											</>
										)}
									</EditableText>
								);
							})}
						</div>

						<div style={OVERVIEW_SECTION_SMALL_STYLE}>
							<EditableText overrideKey='overview.kitchenLayout'>
								<span style={OVERVIEW_BOLD_STYLE}>Kitchen Layout: </span>
								{kitchenLayout}
							</EditableText>
						</div>
					</>
				)}

				{/* Per-island appliance sections */}
				{islandsToRender.map((island, sliceIndex) => {
					const absoluteIndex = startIndex + sliceIndex;
					const islandDescription = getIslandOverviewDescription({ island, kitchen })
						|| `Island ${absoluteIndex + 1}`;
					const appliances = getOverviewAppliances({ island, kitchen });

					return (
						<div key={island.id ?? absoluteIndex} style={OVERVIEW_SECTION_XSMALL_STYLE}>
							<EditableText overrideKey={`overview.island.${island.id}.description`}>
								{(() => {
									const letter = getIslandSuffix({ island, kitchen });
									const prefix = letter ? `${letter} | ` : '';
									const pipeIdx = islandDescription.indexOf(' | ');
									if (pipeIdx < 0) return <span style={OVERVIEW_BOLD_STYLE}>{prefix}{islandDescription}</span>;
									return <>
										<span style={OVERVIEW_BOLD_STYLE}>{prefix}{islandDescription.slice(0, pipeIdx)}</span>
										{islandDescription.slice(pipeIdx)}
									</>;
								})()}
							</EditableText>
							{appliances.length > 0 && (
								<div style={OVERVIEW_INDENTED_STYLE}>
									<EditableText overrideKey={`overview.island.${island.id}.appliances`} mergeStrategy='keyed'>
										{appliances.map((appliance, i) => (
											<React.Fragment key={i}>
												{i > 0 && <br />}
												<span style={appliance.isCustomerSupplied ? OVERVIEW_RED_STYLE : undefined}>
													{appliance.sku}: {appliance.label}
													{appliance.isCustomerSupplied && ' — Customer Supplied'}
												</span>
											</React.Fragment>
										))}
									</EditableText>
								</div>
							)}
						</div>
					);
				})}

				{/* Footer content — last overview page only */}
				{isLastOverviewPage && (
					<>
						{/* marginTop: 14px so the last-appliance -> NSCs gap matches the 14px
						    gap every other section on the slide uses. Adjacent sibling margins
						    collapse to their max, so 7px (last XSMALL appliance mb) vs 14px
						    (this) resolves to 14px — the visible section gap the sheet uses. */}
						<div style={{ ...OVERVIEW_SECTION_SMALL_STYLE, marginTop: '14px' }}>
							{nonStandardChanges.length === 0 ? (
								<EditableText overrideKey='overview.nonStandardChanges.na' mergeStrategy='derived'>
									<span style={OVERVIEW_BOLD_STYLE}>Non-Standard Changes: </span>
									N/A
								</EditableText>
							) : (
								<>
									<EditableText overrideKey='overview.nonStandardChanges.qty' mergeStrategy='derived'>
										<span style={OVERVIEW_BOLD_STYLE}>Non-Standard Changes: </span>
										Qty-{nonStandardChangeCount}
									</EditableText>
									<div style={OVERVIEW_INDENTED_STYLE}>
										<EditableText overrideKey='overview.nonStandardChanges' mergeStrategy='derived'>
											{nonStandardChanges.map((change, i) => (
												<React.Fragment key={i}>
													{i > 0 && <br />}
													<span style={OVERVIEW_BOLD_STYLE}>{change.pageRef}:</span> {change.description}
												</React.Fragment>
											))}
										</EditableText>
								</div>
								</>
							)}
						</div>

						{/* Per-island lines get their own indented block rather than a hanging indent on one:
						    text-indent pulls back only a block's FIRST line, so the same content sat flush
						    while edited (contentEditable breaks with <div>) and 9.5em right once re-rendered
						    from the stored override (<br> lines) — the live-vs-PDF shift in #459. */}
						<div style={OVERVIEW_SECTION_SMALL_STYLE}>
							{(() => {
								const [firstLine, ...perIslandLines] = kitchenLocation.split('\n');
								const legacyLocation = kitchen.blueprintOverrides?.['overview.kitchenLocation'];
								const legacyLocationBase = kitchen.getBlueprintOverrideBase('overview.kitchenLocation');
								return (
									<>
										<EditableText
											overrideKey='overview.kitchenLocation.header'
											legacyOverrideValue={extractLocationHeader(legacyLocation)}
											legacyOverrideBase={extractLocationHeader(legacyLocationBase)}
											legacyClearKey='overview.kitchenLocation'
										>
											<span style={OVERVIEW_BOLD_STYLE}>Kitchen Location: </span>
											{firstLine}
										</EditableText>
										{perIslandLines.length > 0 && (
											<div style={OVERVIEW_INDENTED_STYLE}>
												<EditableText
													overrideKey='overview.kitchenLocation.islands'
													legacyOverrideValue={extractLocationIslands(legacyLocation)}
													legacyOverrideBase={extractLocationIslands(legacyLocationBase)}
													legacyClearKey='overview.kitchenLocation'
												>
													{perIslandLines.map((line, i) => (
														<React.Fragment key={i}>
															{i > 0 && <br />}
															{line}
														</React.Fragment>
													))}
												</EditableText>
											</div>
										)}
									</>
								);
							})()}
						</div>

						<div style={OVERVIEW_PLEASE_NOTE_WRAPPER_STYLE}>
							<EditableText overrideKey='overview.pleaseNote' style={OVERVIEW_PLEASE_NOTE_STYLE}>Please Note:</EditableText>
							<div style={OVERVIEW_INDENTED_RED_STYLE}>
								<EditableText overrideKey='overview.pleaseNoteItems'>
									{(() => {
										const surround = kitchen.material?.surround;
										const isMoksCountertop = (kitchen.material?.countertop?.key ?? '').toLowerCase().startsWith('mok');
										// Every stacked-stone asset entry carries material 'stone' — the semantic flag, immune to key-format drift.
										const isStackedStone = surround?.material === 'stone' || (surround?.displayName ?? '').toLowerCase().includes('stacked stone');
										const lines = [
											// MOKS molded countertops omit the cut-stone 1/4" tolerance note; granite/concrete tops keep it.
											...(isMoksCountertop ? [] : ['Panels can expect an 1/4" variation in size, dimension, and clearances']),
											'All panels are shown from the EXTERIOR side of the panel',
											...(isStackedStone ? ['Varying panel thickness: 1.5" front | 1" sides and back'] : [])
										];
										return lines.map((line, i) => (
											<React.Fragment key={i}>
												{i > 0 && <br />}
												{line}
											</React.Fragment>
										));
									})()}
								</EditableText>
							</div>
						</div>
					</>
				)}
			</div>

			<BlueprintPageFooter pageNumber={page.pageNumber} />
		</BlueprintPageWrapper>
	);
};
